import {
  StickyNote,
  Pin,
  Phone,
  Video,
  Calendar,
  Clock,
  Target,
  Sparkles,
} from 'lucide-react';
import type { CRMNote, NoteType } from '../../types/notes';
import { NOTE_TYPE_COLORS } from '../../types/notes';
import { MeetingReportBadge } from '../crm/MeetingReportBadge';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { MeetingReport } from '../../types/meetingReport';

function getNoteIcon(type: NoteType) {
  switch (type) {
    case 'call_note':
      return <Phone className="w-4 h-4" />;
    case 'meeting_note':
      return <Video className="w-4 h-4" />;
    case 'follow_up':
      return <Calendar className="w-4 h-4" />;
    case 'reminder':
      return <Clock className="w-4 h-4" />;
    default:
      return <StickyNote className="w-4 h-4" />;
  }
}

interface NoteListItemProps {
  note: CRMNote;
  isSelected: boolean;
  onClick: () => void;
  preloadedReport?: MeetingReport | null;
}

export function NoteListItem({ note, isSelected, onClick, preloadedReport }: NoteListItemProps) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left p-3 rounded-lg transition-all
        ${isSelected
          ? 'bg-blue-50 border-blue-200'
          : 'hover:bg-neutral-50'
        }
        ${note.is_pinned && !isSelected ? 'bg-amber-50/50' : ''}
      `}
    >
      <div className="flex items-start gap-2">
        <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${NOTE_TYPE_COLORS[note.note_type]}`}>
          {getNoteIcon(note.note_type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            {note.is_pinned && <Pin className="w-3 h-3 text-amber-500 fill-current shrink-0" />}
            <span className="text-xs text-neutral-400 truncate">
              {formatDistanceToNow(new Date(note.created_at), { addSuffix: true, locale: fr })}
            </span>
          </div>
          <p className="text-sm text-neutral-700 line-clamp-2 leading-snug" data-sensitive-text={note.title ? undefined : ''}>
            {note.title || note.content}
          </p>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            {note.contact && (
              <span className="text-xs text-[#0066FF] bg-blue-50 px-1.5 py-0.5 rounded truncate max-w-[100px]">
                {note.contact.first_name}
              </span>
            )}
            {note.account && (
              <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded truncate max-w-[100px]">
                {note.account.name}
              </span>
            )}
            {note.opportunity && (
              <span
                title={note.opportunity.name}
                className="inline-flex items-center gap-1 text-xs text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded truncate max-w-[140px]"
              >
                <Target className="w-3 h-3 shrink-0" />
                <span className="truncate">{note.opportunity.name}</span>
              </span>
            )}
            {note.lead && (
              <span
                title={note.lead.company_name}
                className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded truncate max-w-[140px]"
              >
                <Sparkles className="w-3 h-3 shrink-0" />
                <span className="truncate">{note.lead.company_name}</span>
              </span>
            )}
            <MeetingReportBadge noteId={note.id} appointmentId={note.appointment_id} variant="badge" preloadedReport={preloadedReport} />
          </div>
        </div>
      </div>
    </button>
  );
}
